import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  useTheme,
  alpha,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Category as CategoryIcon,
  Inventory as InventoryIcon,
  TrendingUp as TrendingUpIcon,
  Business as CompanyIcon
} from '@mui/icons-material';
import { usePageTitle } from 'contexts/PageTitleContext';
import FlexBetween from 'components/flexbox/FlexBetween';
import SearchInput from 'components/input-fields/SearchInput';
import apiService from 'Network/apiService';
import { API_ENDPOINTS } from 'Network/apiEndpoints';
import ProductsList from './ProductsList';
import AddProductModal from './AddProductModal';
import EditProductModal from './EditProductModal';
import { useNavigate } from 'react-router-dom';

const ProductsManagement = () => {
  const theme = useTheme();
  const { setPageTitle } = usePageTitle();
  const navigate = useNavigate();

  // Check if user is Company type
  const isCompanyUser = apiService.isCompanyUser();
  const companyId = apiService.getCompanyId();
  const companyDetails = apiService.getCompanyDetails();
  
  // State
  const [companies, setCompanies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(isCompanyUser ? companyId : 'all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [addProductModalOpen, setAddProductModalOpen] = useState(false);
  const [editProductModalOpen, setEditProductModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Stats state
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalCategories: 0,
    totalStock: 0,
    totalSold: 0
  });

  useEffect(() => {
    setPageTitle('Product Management');
  }, [setPageTitle]);

  // Fetch companies
  const fetchCompanies = async () => {
    try {
      // If Company user, create a company array with their company details
      if (isCompanyUser && companyDetails) {
        setCompanies([companyDetails]);
         setStats(prev => ({ ...prev, totalCompany: 1 }));
        return;
      }
      
      const res = await apiService.get(API_ENDPOINTS.GET_company);
      if (res?.success) {
        const companyList = res.data?.Company || [];
      setCompanies(companyList);
      setStats(prev => ({ ...prev, totalCompany: companyList.length }));
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  // Fetch categories (optionally by company)
  const fetchCategories = async (companyId = 'all') => {
    try {
      let response;
      if (companyId === 'all') {
        response = await apiService.get(API_ENDPOINTS.GET_product_categories);
      } else {
        const userId = localStorage.getItem('user_id');
        response = await apiService.get(`${API_ENDPOINTS.GET_product_category}?company=${companyId}&user_id=${userId}`);
      }
      if (response?.success) {
        const list = response.data?.ProductCategory || [];
        setCategories(list);
        setStats(prev => ({ ...prev, totalCategories: list.length }));
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Fetch products by filters
  const fetchProducts = async (categoryId = selectedCategory, companyId = selectedCompany) => {
    setIsLoading(true);
    try {
      const userId = localStorage.getItem('user_id');
      let url = `${API_ENDPOINTS.GET_all_products}?user_id=${userId}`;
      if (companyId !== 'all') url += `&company=${companyId}`;
      if (categoryId !== 'all') url += `&category=${categoryId}`;

      const response = await apiService.get(url);
      if (response?.success) {
        const productsData = response.data.Product || [];
        setProducts(productsData);
        // Calculate stats
        const totalStock = productsData.reduce((sum, product) => 
          sum + parseFloat(product.analytics?.total_stock || 0), 0);
        const totalSold = productsData.reduce((sum, product) => 
          sum + parseFloat(product.analytics?.total_sold || 0), 0);
        setStats(prev => ({
          ...prev,
          totalProducts: productsData.length,
          totalStock: totalStock,
          totalSold: totalSold
        }));
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Initial loads - only run once
  useEffect(() => {
    const initializeData = async () => {
      await fetchCompanies();
      // For Company users, fetch categories for their company, otherwise fetch all
      if (isCompanyUser && companyId) {
        await fetchCategories(companyId);
      } else {
        await fetchCategories('all');
      }
    };
    
    initializeData();
  }, []); // Empty dependency array - only run once

  // Refetch categories when company changes (only for Admin users)
  useEffect(() => {
    if (!isCompanyUser && selectedCompany) {
      fetchCategories(selectedCompany);
      // Reset category on company change to 'all'
      setSelectedCategory('all');
    }
  }, [selectedCompany, isCompanyUser]);

  // Refetch products when filters change
  useEffect(() => {
    if (selectedCompany) {
      fetchProducts(selectedCategory, selectedCompany);
    }
  }, [selectedCategory, selectedCompany, refreshKey]);

  const handleCompanyChange = (event) => {
    setSelectedCompany(event.target.value);
  };

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
    fetchCompanies();
    if (isCompanyUser && companyId) {
      fetchCategories(companyId);
    } else {
      fetchCategories(selectedCompany);
    }
  };

  const handleAddProduct = () => {
    setAddProductModalOpen(true);
  };

  const handleProductAdded = () => {
    setRefreshKey(prev => prev + 1);
    setAddProductModalOpen(false);
  };

  const handleEditProduct = (product) => {
    setSelectedProduct(product);
    setEditProductModalOpen(true);
  };

  const handleProductUpdated = () => {
    setRefreshKey(prev => prev + 1);
    setEditProductModalOpen(false);
    setSelectedProduct(null);
  };

  // Stats cards data
  const statsCards = [
    {
      title: 'Total Products',
      value: stats.totalProducts,
      icon: <InventoryIcon />,
      color: theme.palette.primary.main,
      bgColor: alpha(theme.palette.primary.main, 0.1)
    },
    {
      title: 'Categories',
      value: stats.totalCategories,
      icon: <CategoryIcon />,
      color: theme.palette.secondary.main,
      bgColor: alpha(theme.palette.secondary.main, 0.1)
    },
    {
      title: 'Total Company',
      value: stats.totalCompany || 0,
      icon: <CompanyIcon />,
      color: theme.palette.info.main,
      bgColor: alpha(theme.palette.info.main, 0.1)
    },
    {
      title: 'Total Sold',
      value: Math.round(stats.totalSold),
      icon: <TrendingUpIcon />,
      color: theme.palette.success.main,
      bgColor: alpha(theme.palette.success.main, 0.1)
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {statsCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                p: 2.5,
                background: `linear-gradient(135deg, ${stat.bgColor} 0%, ${alpha(stat.color, 0.05)} 100%)`,
                border: `1px solid ${alpha(stat.color, 0.1)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 8px 25px ${alpha(stat.color, 0.15)}`
                }
              }}
            >
              <FlexBetween>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {stat.title}
                  </Typography>
                  <Typography variant="h4" fontWeight={700} color={stat.color}>
                    {stat.value.toLocaleString()}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    backgroundColor: alpha(stat.color, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: stat.color
                  }}
                >
                  {stat.icon}
                </Box>
              </FlexBetween>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Controls */}
      <Card sx={{ p: 3, mb: 3 }}>
        {/* Filters Section */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>Filters</Typography>
            <Tooltip title="Refresh">
              <IconButton
                onClick={handleRefresh}
                sx={{
                  backgroundColor: alpha(theme.palette.info.main, 0.1),
                  color: theme.palette.info.main,
                  '&:hover': { backgroundColor: alpha(theme.palette.info.main, 0.2) }
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <SearchInput
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ width: '100%' }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Company</InputLabel>
                <Select 
                  value={selectedCompany} 
                  label="Company" 
                  onChange={handleCompanyChange}
                  disabled={isCompanyUser}
                >
                  {!isCompanyUser && (
                    <MenuItem value="all">
                      <Chip label="All Companies" size="small" color="default" sx={{ mr: 1 }} />
                      All Companies
                    </MenuItem>
                  )}
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      <Chip label={company.name} size="small" color="secondary" variant="outlined" sx={{ mr: 1 }} />
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select value={selectedCategory} label="Category" onChange={handleCategoryChange}>
                  <MenuItem value="all">
                    <Chip label="All Categories" size="small" color="default" sx={{ mr: 1 }} />
                    All Categories
                  </MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      <Chip label={category.name} size="small" color="primary" variant="outlined" sx={{ mr: 1 }} />
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Active Filters */}
          {(selectedCompany !== 'all' || selectedCategory !== 'all' || searchQuery) && (
            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                Active filters:
              </Typography>
              {selectedCompany !== 'all' && (
                <Chip
                  label={`Company: ${companies.find(c => c.id.toString() === selectedCompany.toString())?.name || selectedCompany}`}
                  onDelete={() => setSelectedCompany('all')}
                  color="secondary"
                  variant="outlined"
                  size="small"
                />
              )}
              {selectedCategory !== 'all' && (
                <Chip
                  label={`Category: ${categories.find(c => c.id.toString() === selectedCategory.toString())?.name || selectedCategory}`}
                  onDelete={() => setSelectedCategory('all')}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              )}
              {searchQuery && (
                <Chip
                  label={`Search: "${searchQuery}"`}
                  onDelete={() => setSearchQuery('')}
                  color="default"
                  variant="outlined"
                  size="small"
                />
              )}
            </Box>
          )}
        </Box>

        {/* Actions Section */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>Actions</Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddProduct}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`
                }
              }}
            >
              Add Product
            </Button>
            <Button
              variant="outlined"
              startIcon={<CategoryIcon />}
              onClick={() => navigate('/dashboard/company-category-management')}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3, py: 1.5 }}
            >
              Manage Companies & Categories
            </Button>
          </Box>
        </Box>
      </Card>

      {/* Products List */}
      <Card>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <ProductsList
            products={products}
            categories={categories}
            searchQuery={searchQuery}
            selectedCategory={selectedCategory}
            onRefresh={handleRefresh}
            onEditProduct={handleEditProduct}
          />
        )}
      </Card>

      {/* Add Product Modal */}
      <AddProductModal
        open={addProductModalOpen}
        onClose={() => setAddProductModalOpen(false)}
        onProductAdded={handleProductAdded}
        categories={categories}
      />

      {/* Edit Product Modal */}
      <EditProductModal
        open={editProductModalOpen}
        onClose={() => {
          setEditProductModalOpen(false);
          setSelectedProduct(null);
        }}
        onProductUpdated={handleProductUpdated}
        product={selectedProduct}
      />
    </Box>
  );
};

export default ProductsManagement; 